'use client';

import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Plus, Minus, ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';

// Accordion Root Component
function Accordion({
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

// Accordion Item Component
function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn('border-b last:border-b-0', className)}
      {...props}
    />
  );
}

// Accordion Trigger Component with icon before the text.
function AccordionTrigger({
  className,
  children,
  isChevron,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  isChevron?: boolean;
}) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        // Adding "group" allows us to access data-state on nested elements.
        className={cn(
          'group flex items-center gap-4  rounded-md py-4 text-left text-sm font-medium transition-all focus:outline-none hover:underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180',
          className
        )}
        {...props}
      >
        {!isChevron && (
          <div className="w-5 h-5 flex-shrink-0">
            {/* Plus icon: Visible when closed */}
            <Plus
              className="transition duration-200 group-data-[state=open]:hidden group-data-[state=closed]:block"
              size={20}
            />
            {/* Minus icon: Visible when open */}
            <Minus
              className="transition duration-200 hidden group-data-[state=open]:block group-data-[state=closed]:hidden"
              size={20}
            />
          </div>
        )}
        <span>{children}</span>

        {isChevron && (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
        )}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

// Accordion Content Component
function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={cn(
        'overflow-hidden text-sm transition-all',
        'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
        className
      )}
      {...props}
    >
      <div className="pt-0 pb-4">{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
